<template>
  <v-chart
    v-if="!loadingTable"
    :option="option"
    autoresize
    style="width: 100%; max-height: 500px;"
  />
  <div
    v-else
    style="display: flex; justify-content: center; align-items: center; padding: 10rem; margin-left: 3rem;"
  >
    <Spinner />
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  type ComputedRef,
  type Ref,
  unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { type ProviderParams, useProvider } from '@utils/table/hooks/provider';
import Spinner from '@utils/Spinner.vue';
import VChart from 'vue-echarts';
import type {
  HistoryDriver,
  HistoryGeneral,
  HistoryUsageStatistic,
  HydraCollection,
  HydraMember,
} from '@/entities/Entities';
import { CanvasRenderer } from 'echarts/renderers';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { use } from 'echarts/core';

use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
]);

const { t } = useI18n();

interface Props {
  dataType: 'driver' | 'general' | 'usage';
  visibleColumns: Ref<Record<string, boolean>>;
  providerParams: ProviderParams | Ref;
}

const props = defineProps<Props>();

const providerParamsFull = computed(() => ({
  ...unref(props.providerParams),
  pagination: false,
}));

const endpoint = computed(() => {
  if (props.dataType === 'driver') return '/history_drivers';
  if (props.dataType === 'general') return '/history_generals';
  return '/history_usage_statistics';
});

type DataType = HistoryDriver | HistoryGeneral | HistoryUsageStatistic;

const {
  data: items,
  loading: loadingTable,
} = useProvider<HydraCollection<DataType>>(endpoint.value, providerParamsFull);

const records: ComputedRef<Array<HydraMember & DataType> | undefined> = computed(() => unref(items)?.['hydra:member']);

// Transform data for chart
function transformDataForChart(data: DataType[]) {
  if (!Array.isArray(data)) {
    return [];
  }

  const result: Record<string, Array<[Date, number]>> = {};

  data.forEach((item) => {
    const date = new Date(item.recordingDate);

    Object.entries(item).forEach(([key, value]) => {
      if (typeof value === 'number') {
        const regionName = item?.region?.name ?? 'Gesamt';
        const resultKey = `${key} ${regionName}`;

        if (!result[resultKey]) {
          result[resultKey] = [];
        }

        result[resultKey].push([date, value]);
      }
    });
  });

  return Object.keys(result).map((key) => ({
    name: key,
    type: 'line',
    data: result[key],
  }));
}

const chartSeries = computed(() => {
  const sortedData = (unref(records) ?? []).slice()
    .sort((a: DataType, b: DataType) => new Date(a.recordingDate).getTime() - new Date(b.recordingDate).getTime());

  const chartDataColFiltered = transformDataForChart(sortedData)
    .filter((series) => {
      const metricName = series.name.split(' ')[0];
      return unref(props.visibleColumns)[metricName] && metricName !== 'recordingDate';
    });

  return chartDataColFiltered.map((s) => {
    const [metric, region] = s.name.split(' ');
    return {
      name: `${t(`pages.history.${metric}`)} ${region}`,
      type: 'line',
      data: s.data,
    };
  });
});

const option = computed(() => ({
  title: {
    text: props.dataType === 'driver'
      ? t('pages.history.graphTitleDriver')
      : props.dataType === 'general'
        ? t('pages.history.graphTitleGeneral')
        : t('pages.history.graphTitleUsage'),
    left: 'center',
  },
  tooltip: {
    trigger: 'axis',
    formatter: (params: any[]) =>
      params
        .map((param) => {
          const date = new Date(param.data[0]).toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
          return `${param.seriesName} <br/> ${date} : ${param.data[1]}`;
        })
        .join('<br/>'),
    axisPointer: {
      type: 'cross',
    },
  },
  legend: {
    top: 'bottom',
    itemGap: 10,
    textStyle: {
      fontSize: 12,
    },
  },
  grid: {
    left: '3%',
    right: '4%',
    bottom: '15%',
    containLabel: true,
  },
  xAxis: {
    type: 'time',
    name: t('pages.history.graphTitle2'),
    axisLabel: {
      formatter: '{yyyy}-{MM}-{dd}',
    },
  },
  yAxis: {
    type: 'value',
    name: t('pages.history.amount'),
  },
  series: chartSeries.value,
}));

</script>
